<template>
  <div class="product-details">
    <base-accordion multiple>
      <base-accordion-item
        :name="$string('product.details.description')"
        transition-height
      >
        <template #headerLabel>
          <span
            class="text-heading-m"
            v-text="$string('product.details.description')"
          />
        </template>
        <rte-content
          class="text-body-s-regular"
          :content="product.description"
        />

        <div
          v-if="additionalContent"
          class="
            product-details__additional-content
            text-body-s-regular
            text-body-m-regular-desktop
          "
          v-html="additionalContent"
        />

        <div
          v-if="showProductCodes && variant?.sku"
          class="product-details__product-code-container"
        >
          <p
            class="product-details__product-code text-body-s-regular"
            v-html="
              `
                ${$string('product.product_code')}:
                <span class='product-details__product-code-sku'>
                  ${showSku}
                </span>
              `
            "
          />
        </div>
      </base-accordion-item>

      <base-accordion-item
        v-if="localSpecifications?.length"
        :name="$string('product.details.details')"
        transition-height
      >
        <template #headerLabel>
          <span
            class="text-heading-m"
            v-text="$string('product.details.details')"
          />
        </template>
        <div class="product-details__specs-wrapper">
          <table class="product-details__specs-table">
            <tr
              v-for="spec of localSpecifications"
              :key="spec.specification"
            >
              <th
                class="text-body-s-bold"
                v-text="spec.title"
              />
              <td
                class="text-body-s-regular"
                v-html="spec.value"
              />
            </tr>
          </table>
        </div>
      </base-accordion-item>

      <base-accordion-item
        v-if="deliveryContent"
        :name="$string('product.details.delivery_and_returns')"
        transition-height
      >
        <template #headerLabel>
          <span
            class="text-heading-m"
            v-text="$string('product.details.delivery_and_returns')"
          />
        </template>
        <div class="product-details__delivery-content-wrapper">
          <rte-content
            class="text-body-s-regular"
            :content="deliveryContent"
          />
        </div>
      </base-accordion-item>
    </base-accordion>
  </div>
</template>

<script>

/**
 * Vue: Product details (product-details)
 * -----------------------------------------------------------------------------
 * Displays product details like description on the PDP.
 * - Component settings: snippet / dynamic / async / scroll
 *
 */
import BaseAccordion from '~async/base-accordion/base-accordion'
import BaseAccordionItem from '~async/base-accordion/base-accordion-item'
import RteContent from '~async/rte-content/rte-content'

export default {
  name: 'ProductDetails',

  components: {
    BaseAccordion,
    BaseAccordionItem,
    RteContent,
  },

  props: {
    additionalContent: {
      type: String,
      default: '',
    },
    deliveryContent: {
      type: String,
      default: '',
    },
    product: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    variant: {
      type: [Boolean, null, Object],
      default: false,
      required: true,
    },
  },

  data() {
    return {}
  },

  computed: {
    showProductCodes() {
      return !this.product?.giftCard
    },

    /**
     * Show Sku.
     */
    showSku() {
      if (this.product?.personalisation?.enabled) {
        // Format sku e.g to show 3POS010063 instead of 3POS010063text00
        const formatSku = this.variant?.sku.toLowerCase().split('text')

        return formatSku[0]
      }

      return this.variant?.sku
    },

    /**
     * Compute local specifications.
     * - Loop through the specifications and add title attribute using locale
     *   strings.
     * @returns {Array}
     */
    localSpecifications() {
      return this.product?.specifications?.map((specificationItem) => {
        const title = this.$string(`product.specifications.${specificationItem.specification}`)

        return {
          ...specificationItem,
          title,
        }
      })
    },
  },
}
</script>

<style lang="scss">
@import './product-details';
</style>
